import {
  Database,
  Brain,
  HelpCircle,
  BookOpen,
  Zap,
  BarChart3,
  Gauge,
  FileText,
  Car,
  Settings,
  Activity,
  Flag,
  TrendingUp,
  Scan,
  ImageIcon,
  Sparkles,
  Trophy,
  Scale,
  HeartPulse,
  DollarSign,
  Search,
  Compass,
  ListChecks,
  CheckCircle2,
  Clock,
  Target,
  ClipboardList,
  ClipboardCheck,
  EyeOff,
  ScrollText,
  Building2,
  Send,
  Gift,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SHOW_QUEUE } from '@/config/env';

export interface NavigationItem {
  label: string;
  // `to` is omitted for parent items that only group children — the sidebar
  // renders those as an expandable section instead of a Link.
  to?: string;
  icon: LucideIcon;
  badge?: string;
  children?: NavigationItem[];
}

export interface NavigationGroup {
  title: string;
  items: NavigationItem[];
}

export type NavigationTabKey = 'app' | 'playground';

export interface NavigationTab {
  key: NavigationTabKey;
  label: string;
  defaultPath: string;
  groups: NavigationGroup[];
}

const CASE_ANALYSIS_NAVIGATION_ITEM: NavigationItem = {
  label: 'Case Analysis',
  icon: Brain,
  children: [
    {
      label: 'Liability',
      to: '/prompts/case-analysis/liability',
      icon: Scale,
    },
    {
      label: 'Crash Severity',
      to: '/prompts/case-analysis/crash-severity',
      icon: Activity,
    },
    {
      label: 'Injury Causation',
      to: '/prompts/case-analysis/injury-causation',
      icon: HeartPulse,
    },
    {
      label: 'Injury Exposure / Value',
      to: '/prompts/case-analysis/injury-exposure',
      icon: DollarSign,
    },
    {
      label: 'Strategic Synthesis',
      to: '/prompts/case-analysis/strategic-synthesis',
      icon: Sparkles,
    },
  ],
};

const QUEUE_NAVIGATION_GROUP: NavigationGroup = {
  title: 'Queue',
  items: [
    {
      label: 'Evidence Review',
      to: '/queue/evidence-review',
      icon: ClipboardList,
    },
    {
      label: 'DeltaV Review',
      to: '/queue/deltav-review',
      icon: ListChecks,
    },
    {
      label: 'Confirmed by Reviewer',
      to: '/queue/confirmed',
      icon: CheckCircle2,
    },
    {
      label: 'Auto-Confirmed',
      to: '/queue/auto-confirmed',
      icon: Clock,
    },
  ],
};

export const APP_NAVIGATION_GROUPS: NavigationGroup[] = [
  // The Queue is gated behind the VITE_SHOW_QUEUE feature flag (default off).
  ...(SHOW_QUEUE ? [QUEUE_NAVIGATION_GROUP] : []),
  {
    title: 'Accounts',
    items: [
      {
        label: 'Organizations',
        to: '/organizations',
        icon: Building2,
      },
      {
        label: 'Firm Invitations',
        to: '/firm-invitations',
        icon: Send,
      },
      {
        label: 'Referrals',
        to: '/referrals',
        icon: Gift,
      },
    ],
  },
  {
    title: 'Billing',
    items: [
      {
        label: 'BNPL Funding',
        to: '/bnpl-invoices',
        icon: DollarSign,
      },
    ],
  },
  {
    title: 'Prompts',
    items: [
      {
        label: 'TCR Extraction',
        to: '/prompts/tcr-extraction',
        icon: FileText,
      },
      CASE_ANALYSIS_NAVIGATION_ITEM,
    ],
  },
  {
    title: 'Data Management',
    items: [
      {
        label: 'Question Bank',
        to: '/question-bank',
        icon: HelpCircle,
      },
      {
        label: 'Research Papers',
        to: '/research-papers',
        icon: BookOpen,
      },
    ],
  },
  {
    title: 'LLM Spend',
    items: [
      {
        label: 'Usage',
        to: '/llm-usage',
        icon: Gauge,
      },
    ],
  },
];

export const PLAYGROUND_NAVIGATION_GROUPS: NavigationGroup[] = [
  {
    title: 'Prompts',
    items: [
      {
        label: 'Damage Score via Images',
        to: '/prompts/damage-scores',
        icon: Gauge,
      },
      {
        label: 'Delta-V via EDR',
        to: '/prompts/edr-extraction',
        icon: BarChart3,
      },
      {
        label: 'Vehicle Extraction',
        to: '/prompts/vehicle-extraction',
        icon: Car,
      },
      {
        label: 'Vehicle Classification',
        to: '/prompts/vehicle-classification',
        icon: Settings,
      },
    ],
  },
  {
    title: 'Training Datasets',
    items: [
      {
        label: 'Overview',
        to: '/training-datasets',
        icon: Database,
      },
      {
        label: 'Case Reviews',
        to: '/training-datasets/review',
        icon: ClipboardCheck,
      },
      {
        label: 'Cases',
        to: '/datasets/cases',
        icon: Database,
      },
      {
        label: 'Flagged',
        to: '/datasets/flagged',
        icon: Flag,
      },
    ],
  },
  {
    title: 'Models',
    items: [
      {
        label: 'Dataset Breakdown',
        to: '/dataset-breakdown',
        icon: BarChart3,
      },
      {
        label: 'Models',
        to: '/ml-training',
        icon: Zap,
      },
      {
        label: 'Leaderboard',
        to: '/ml-training/leaderboard',
        icon: Trophy,
      },
      {
        label: 'Benchmarks',
        to: '/evaluations/delta-v/benchmark',
        icon: Target,
      },
    ],
  },
  {
    title: 'Evaluations',
    items: [
      {
        label: 'Blind Evaluation',
        to: '/evaluations/blind',
        icon: EyeOff,
      },
      {
        label: 'Prompt Evaluation',
        to: '/evaluations/prompts',
        icon: ScrollText,
      },
      {
        label: 'Damage Score',
        to: '/evaluations/damage-score',
        icon: Gauge,
      },
      {
        label: 'Delta-V Runs',
        to: '/evaluations/delta-v',
        icon: TrendingUp,
      },
      {
        label: 'PDOF',
        to: '/evaluations/pdof',
        icon: Compass,
      },
      {
        label: 'EDR Extraction',
        to: '/evaluations/edr-extraction',
        icon: BarChart3,
      },
    ],
  },
  {
    title: 'Workflows',
    items: [
      {
        label: 'Biomechanics Engine',
        to: '/biomechanics-analysis-v2',
        icon: Sparkles,
      },
      {
        label: 'Technical Report',
        to: '/technical-report',
        icon: FileText,
      },
      {
        label: 'Find Similar Cases',
        to: '/find-similar-cases',
        icon: Search,
      },
      {
        label: 'Part Segmentation',
        to: '/segmentation',
        icon: Scan,
        badge: 'Disabled',
      },
      {
        label: 'Image Quality',
        to: '/image-quality-assessment',
        icon: ImageIcon,
      },
      {
        label: 'Intake Classification',
        to: '/intake-classification',
        icon: ClipboardList,
      },
      {
        label: 'PDOF Calculator',
        to: '/pdof-calculator',
        icon: Compass,
      },
      {
        label: 'Delta-V Calculator',
        to: '/delta-v-calculator',
        icon: Gauge,
      },
      {
        label: 'Crash Severity',
        to: '/crash-severity',
        icon: Activity,
      },
      {
        label: 'Public Analysis',
        to: '/public-analysis',
        icon: Zap,
      },
      {
        label: 'IIHS Clearance',
        to: '/iihs-clearance',
        icon: Car,
      },
      {
        label: 'AIS Injury Risk',
        to: '/ais-injury-risk',
        icon: TrendingUp,
      },
    ],
  },
];

// Where the App tab and the root URL land by default: the Evidence Review
// queue when the queue is enabled, otherwise the first always-visible item.
export const APP_DEFAULT_PATH = SHOW_QUEUE
  ? '/queue/evidence-review'
  : '/prompts/case-analysis/liability';

export const NAVIGATION_TABS: NavigationTab[] = [
  {
    key: 'app',
    label: 'App',
    defaultPath: APP_DEFAULT_PATH,
    groups: APP_NAVIGATION_GROUPS,
  },
  {
    key: 'playground',
    label: 'Data',
    defaultPath: '/training-datasets',
    groups: PLAYGROUND_NAVIGATION_GROUPS,
  },
];

export const NAVIGATION_GROUPS = PLAYGROUND_NAVIGATION_GROUPS;
